/** Rule builder dialog — TRIGGER → CONDITIONS → ACTIONS, with dry-run preview. */
import { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loader2, Plus, Trash2, FlaskConical, Save, Zap, Filter, Play } from "lucide-react";
import { condText } from "./automationUtils";

const Sel = ({ value, onChange, children, testId, className = "" }) => (
  <select value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={testId}
    className={`h-8 rounded-md border border-slate-200 bg-white px-2 text-[12px] text-slate-800 focus:outline-none focus:ring-2 focus:ring-sky-200 ${className}`}>
    {children}
  </select>
);

const Field = ({ label, children, hint }) => (
  <label className="block space-y-1">
    <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">{label}</span>
    {children}
    {hint && <span className="block text-[10px] text-slate-400">{hint}</span>}
  </label>
);

const Step = ({ n, icon: Icon, title, sub, children }) => (
  <section className="rounded-lg border border-slate-200 bg-white p-4 space-y-3">
    <div className="flex items-center gap-2">
      <span className="grid h-6 w-6 place-items-center rounded-full bg-slate-900 font-mono text-[10px] font-bold text-white">{n}</span>
      <Icon className="h-3.5 w-3.5 text-sky-600" />
      <h3 className="text-[12px] font-bold uppercase tracking-wider text-slate-900">{title}</h3>
      <span className="text-[11px] text-slate-400">{sub}</span>
    </div>
    {children}
  </section>
);

function TriggerParams({ trig, params, onChange }) {
  if (!trig?.params?.length) return <p className="text-[11px] text-slate-400">No trigger settings — all matching records are scanned.</p>;
  return (
    <div className="grid sm:grid-cols-2 gap-3">
      {trig.params.map((p) => p.type === "multi" ? (
        <Field key={p.key} label={p.label} hint="Leave all unticked to use the default set">
          <div className="flex flex-wrap gap-1">
            {p.options.map((o) => {
              const on = (params[p.key] || []).includes(o);
              return (
                <button type="button" key={o} data-testid={`trig-opt-${o}`}
                  onClick={() => onChange({ ...params, [p.key]: on ? params[p.key].filter((x) => x !== o) : [...(params[p.key] || []), o] })}
                  className={`rounded-full border px-2 py-0.5 font-mono text-[10px] ${on ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"}`}>{o}</button>
              );
            })}
          </div>
        </Field>
      ) : (
        <Field key={p.key} label={p.label}>
          <Input type="number" className="h-8 text-[12px]" value={params[p.key] ?? p.default ?? ""} data-testid={`trig-param-${p.key}`}
            onChange={(e) => onChange({ ...params, [p.key]: e.target.value === "" ? null : Number(e.target.value) })} />
        </Field>
      ))}
    </div>
  );
}

function ConditionRow({ c, fields, ops, onChange, onRemove, idx }) {
  const ftype = fields[c.field];
  const noVal = ["empty", "not_empty"].includes(c.op);
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid={`cond-row-${idx}`}>
      <Sel value={c.field} onChange={(v) => onChange({ ...c, field: v })} testId={`cond-field-${idx}`} className="min-w-[160px]">
        {Object.keys(fields).map((f) => <option key={f} value={f}>{f}</option>)}
      </Sel>
      <Sel value={c.op} onChange={(v) => onChange({ ...c, op: v })} testId={`cond-op-${idx}`}>
        {Object.entries(ops).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
      </Sel>
      {!noVal && (ftype === "bool" ? (
        <Sel value={String(c.value)} onChange={(v) => onChange({ ...c, value: v === "true" })} testId={`cond-value-${idx}`}><option value="true">true</option><option value="false">false</option></Sel>
      ) : (
        <Input className="h-8 w-40 text-[12px]" type={ftype === "number" && !["in", "not_in"].includes(c.op) ? "number" : "text"} value={c.value ?? ""} data-testid={`cond-value-${idx}`}
          placeholder={["in", "not_in"].includes(c.op) ? "a, b, c" : "value"}
          onChange={(e) => onChange({ ...c, value: ftype === "number" && !["in", "not_in"].includes(c.op) ? (e.target.value === "" ? "" : Number(e.target.value)) : e.target.value })} />
      ))}
      <button type="button" onClick={onRemove} className="text-slate-400 hover:text-rose-600" data-testid={`cond-remove-${idx}`}><Trash2 className="h-3.5 w-3.5" /></button>
    </div>
  );
}

function ActionRow({ a, cat, onChange, onRemove, idx, fields }) {
  const meta = cat.actions.find((x) => x.key === a.type);
  const p = a.params || {};
  const set = (k, v) => onChange({ ...a, params: { ...p, [k]: v } });
  const has = (k) => meta?.params?.includes(k);
  const vars = Object.keys(fields).map((f) => `{${f}}`).join(" ");
  return (
    <div className="rounded-md border border-slate-200 bg-slate-50/60 p-3 space-y-2" data-testid={`action-row-${idx}`}>
      <div className="flex items-center gap-2">
        <Sel value={a.type} onChange={(v) => onChange({ type: v, params: {} })} testId={`action-type-${idx}`} className="min-w-[220px] font-semibold">
          {cat.actions.map((x) => <option key={x.key} value={x.key}>{x.label}</option>)}
        </Sel>
        <button type="button" onClick={onRemove} className="ml-auto text-slate-400 hover:text-rose-600" data-testid={`action-remove-${idx}`}><Trash2 className="h-3.5 w-3.5" /></button>
      </div>
      <div className="grid sm:grid-cols-2 gap-2">
        {has("to") && (
          <Field label={a.type === "request_approval" ? "Approvers" : "Send to"}>
            <Sel value={p.to || (a.type === "request_approval" ? "management" : "owner")} onChange={(v) => set("to", v)} testId={`action-to-${idx}`} className="w-full">
              {Object.entries(cat.targets).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </Sel>
          </Field>
        )}
        {has("emails") && p.to === "custom" && (
          <Field label="Emails (comma separated)">
            <Input className="h-8 text-[12px]" value={(p.emails || []).join(", ")} data-testid={`action-emails-${idx}`}
              onChange={(e) => set("emails", e.target.value.split(",").map((s) => s.trim()).filter(Boolean))} />
          </Field>
        )}
        {has("kind") && (
          <Field label="Task type">
            <Sel value={p.kind || "generic"} onChange={(v) => set("kind", v)} testId={`action-kind-${idx}`} className="w-full">
              {cat.task_kinds.map((k) => <option key={k} value={k}>{k.replace(/_/g, " ")}</option>)}
            </Sel>
          </Field>
        )}
        {has("priority") && (
          <Field label="Task priority">
            <Sel value={p.priority || "medium"} onChange={(v) => set("priority", v)} testId={`action-priority-${idx}`} className="w-full">
              {cat.priorities.map((k) => <option key={k} value={k}>{k}</option>)}
            </Sel>
          </Field>
        )}
        {has("due_in_days") && (
          <Field label="Due in (days)"><Input type="number" className="h-8 text-[12px]" value={p.due_in_days ?? 1} onChange={(e) => set("due_in_days", Number(e.target.value))} data-testid={`action-due-${idx}`} /></Field>
        )}
        {has("amount_inr") && (
          <Field label="Estimated value (₹)"><Input type="number" className="h-8 text-[12px]" value={p.amount_inr ?? 0} onChange={(e) => set("amount_inr", Number(e.target.value))} data-testid={`action-amount-${idx}`} /></Field>
        )}
      </div>
      {has("title") && <Field label={a.type === "create_opportunity" ? "Opportunity name" : "Title"} hint={`Variables: ${vars}`}>
        <Input className="h-8 text-[12px]" value={p.title || ""} onChange={(e) => set("title", e.target.value)} data-testid={`action-title-${idx}`} placeholder="e.g. Quote {code} idle for {idle_days} days" /></Field>}
      {has("body") && <Field label="Message"><Textarea rows={2} className="text-[12px]" value={p.body || ""} onChange={(e) => set("body", e.target.value)} data-testid={`action-body-${idx}`} /></Field>}
      {has("reason") && <Field label="Escalation reason" hint={`Variables: ${vars}`}><Input className="h-8 text-[12px]" value={p.reason || ""} onChange={(e) => set("reason", e.target.value)} data-testid={`action-reason-${idx}`} /></Field>}
    </div>
  );
}

function PreviewPanel({ preview, ops, rule }) {
  if (!preview) return null;
  return (
    <div className="rounded-lg border border-sky-200 bg-sky-50/60 p-3 space-y-2" data-testid="rule-preview">
      <div className="flex flex-wrap items-center gap-3 text-[12px]">
        <span className="font-semibold text-sky-900">Dry run</span>
        <span>Scanned <b data-testid="preview-scanned">{preview.scanned}</b></span>
        <span>Matched <b data-testid="preview-matched">{preview.matched}</b></span>
        <span>Would fire <b data-testid="preview-fired">{preview.fired}</b>{preview.capped && <span className="text-amber-700"> (capped by max per run)</span>}</span>
        {preview.skipped_cooldown > 0 && <span className="text-slate-500">{preview.skipped_cooldown} in cooldown</span>}
        {preview.errors?.length > 0 && <span className="text-rose-600">{preview.errors[0]}</span>}
      </div>
      {rule.conditions.length > 0 && <div className="text-[11px] text-slate-500">Conditions: {rule.conditions.map((c) => condText(c, ops)).join(" AND ")}</div>}
      <div className="max-h-44 overflow-auto divide-y divide-sky-100">
        {preview.samples.map((s, i) => (
          <div key={i} className="flex items-center gap-2 py-1 text-[11px]">
            <span className="font-mono text-slate-800">{s.title}</span>
            <span className="text-slate-500 truncate">{s.customer || ""}</span>
            <span className="ml-auto font-mono text-[10px] text-slate-400 truncate max-w-[45%]">
              {Object.entries(s.fields || {}).filter(([k]) => rule.conditions.some((c) => c.field === k)).map(([k, v]) => `${k}=${v}`).join(" · ")}
            </span>
          </div>
        ))}
        {preview.samples.length === 0 && <div className="py-2 text-[11px] text-slate-500">No records would fire right now.</div>}
      </div>
    </div>
  );
}

export function RuleBuilder({ open, onClose, cat, initial, onSaved }) {
  const [rule, setRule] = useState(initial);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const trig = cat.triggers.find((t) => t.key === rule.trigger.type);
  const fields = trig?.fields || {};
  const upd = (patch) => { setRule((r) => ({ ...r, ...patch })); setPreview(null); };
  const setTrigger = (type) => upd({ trigger: { type, params: {} }, conditions: [] });

  const body = () => ({ ...rule, owner_email: rule.owner_email || null, conditions: rule.conditions.filter((c) => c.field) });
  const doPreview = async () => {
    setBusy(true);
    try { const { data } = await api.post("/automation/preview", body()); setPreview(data); }
    catch (e) { toast.error(e.response?.data?.detail || "Preview failed"); } finally { setBusy(false); }
  };
  const save = async () => {
    setBusy(true);
    try {
      const data = rule.id ? (await api.put(`/automation/rules/${rule.id}`, body())).data : (await api.post("/automation/rules", body())).data;
      toast.success(rule.id ? "Automation updated" : "Automation created"); onSaved(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="rule-builder">
        <DialogHeader><DialogTitle className="font-heading text-lg">{rule.id ? "Edit automation" : "New automation"}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="grid sm:grid-cols-[1fr_180px_140px] gap-3">
            <Field label="Automation name"><Input className="h-8 text-[12px]" value={rule.name} onChange={(e) => upd({ name: e.target.value })} data-testid="rule-name" placeholder="IF … THEN …" /></Field>
            <Field label="Owner"><Input className="h-8 text-[12px]" value={rule.owner_email || ""} onChange={(e) => upd({ owner_email: e.target.value })} data-testid="rule-owner" placeholder="you (default)" /></Field>
            <Field label="Priority">
              <Sel value={rule.priority} onChange={(v) => upd({ priority: v })} testId="rule-priority" className="w-full">{cat.priorities.map((p) => <option key={p} value={p}>{p}</option>)}</Sel>
            </Field>
          </div>
          <Field label="Description"><Input className="h-8 text-[12px]" value={rule.description || ""} onChange={(e) => upd({ description: e.target.value })} data-testid="rule-description" /></Field>

          <Step n={1} icon={Zap} title="Trigger" sub="WHEN this kind of record exists">
            <div className="grid sm:grid-cols-3 gap-1.5">
              {cat.triggers.map((t) => (
                <button type="button" key={t.key} onClick={() => setTrigger(t.key)} data-testid={`trigger-${t.key}`}
                  className={`rounded-md border px-3 py-2 text-left transition-colors ${rule.trigger.type === t.key ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 bg-white hover:border-slate-400"}`}>
                  <div className="text-[12px] font-semibold">{t.label}</div>
                  <div className={`text-[10px] ${rule.trigger.type === t.key ? "text-slate-300" : "text-slate-400"}`}>{t.group}</div>
                </button>
              ))}
            </div>
            <TriggerParams trig={trig} params={rule.trigger.params || {}} onChange={(p) => upd({ trigger: { ...rule.trigger, params: p } })} />
          </Step>

          <Step n={2} icon={Filter} title="Conditions" sub="IF all of these are true (optional)">
            {rule.conditions.map((c, i) => (
              <ConditionRow key={i} idx={i} c={c} fields={fields} ops={cat.operators}
                onChange={(nc) => upd({ conditions: rule.conditions.map((x, j) => (j === i ? nc : x)) })}
                onRemove={() => upd({ conditions: rule.conditions.filter((_, j) => j !== i) })} />
            ))}
            <Button type="button" variant="outline" size="sm" data-testid="cond-add"
              onClick={() => upd({ conditions: [...rule.conditions, { field: Object.keys(fields)[0], op: "eq", value: "" }] })}><Plus className="h-3.5 w-3.5 mr-1" />Add condition</Button>
          </Step>

          <Step n={3} icon={Play} title="Actions" sub="THEN do this">
            {rule.actions.map((a, i) => (
              <ActionRow key={i} idx={i} a={a} cat={cat} fields={fields}
                onChange={(na) => upd({ actions: rule.actions.map((x, j) => (j === i ? na : x)) })}
                onRemove={() => upd({ actions: rule.actions.filter((_, j) => j !== i) })} />
            ))}
            <Button type="button" variant="outline" size="sm" data-testid="action-add"
              onClick={() => upd({ actions: [...rule.actions, { type: "notify", params: { to: "owner", title: "", body: "" } }] })}><Plus className="h-3.5 w-3.5 mr-1" />Add action</Button>
          </Step>

          <section className="rounded-lg border border-slate-200 bg-white p-4 grid sm:grid-cols-4 gap-3">
            <Field label="Check every (min)" hint="How often the engine scans"><Input type="number" className="h-8 text-[12px]" value={rule.interval_minutes} onChange={(e) => upd({ interval_minutes: Number(e.target.value) })} data-testid="rule-interval" /></Field>
            <Field label="Cooldown (days)" hint="0 = fire once per record"><Input type="number" className="h-8 text-[12px]" value={rule.cooldown_days} onChange={(e) => upd({ cooldown_days: Number(e.target.value) })} data-testid="rule-cooldown" /></Field>
            <Field label="Max per run" hint="Burst protection"><Input type="number" className="h-8 text-[12px]" value={rule.max_per_run} onChange={(e) => upd({ max_per_run: Number(e.target.value) })} data-testid="rule-max" /></Field>
            <Field label="Enabled">
              <div className="flex items-center gap-2 h-8"><Switch checked={rule.enabled} onCheckedChange={(v) => upd({ enabled: v })} data-testid="rule-enabled" /><span className="text-[12px]">{rule.enabled ? "On" : "Off"}</span></div>
            </Field>
          </section>

          <PreviewPanel preview={preview} ops={cat.operators} rule={rule} />

          <div className="flex flex-wrap items-center gap-2 justify-end">
            <Button type="button" variant="outline" onClick={doPreview} disabled={busy || !rule.actions.length} data-testid="rule-preview-btn">
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <FlaskConical className="h-3.5 w-3.5 mr-1" />}Test (dry run)
            </Button>
            <Button type="button" onClick={save} disabled={busy || !rule.name.trim() || !rule.actions.length} data-testid="rule-save"><Save className="h-3.5 w-3.5 mr-1" />{rule.id ? "Save changes" : "Create automation"}</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
